import { FullPost, FullPromotedPost } from 'model';
type RandomGenerator = () => number;
/**
 *
 * @param { FullPost[] } feed Current feed
 * @param { FullPromotedPost[] } promoted Current list of promoted posts
 * @param { number } density number of posts per promoted post
 * @param { RandomGenerator } random random number generator to use (defaults to Math.random)
 * @returns { FullPost[] } the current feed with promoted posts injected
 */
export function injectPromotedPosts(
	feed: FullPost[],
	promoted: FullPromotedPost[],
	density: number,
	random: RandomGenerator = Math.random
): FullPost[] {
	if (promoted.length === 0 || feed.length === 0 || density === 0) {
		return feed;
	}
	let merged: FullPost[] = [];
	let promotedIdx = 0;
	let feedIdx = 0;
	const feedIds = new Set(feed.map(({ _id }) => _id));
	const filtered = promoted.filter(({ _id }) => !feedIds.has(_id));
	// Figure out the number of promotions that need to be injected based on the density, never less than one
	const insertTotal = feed.length / density;

	if (insertTotal < 1) {
		// Too few items in feed to satisfy density use probability
		// ie feed.length = 1, density = 2 => 50% chance of a promoted post
		if (random() < insertTotal) {
			// generate a random index to inject
			const idx = Math.round(random() * Math.max(promoted.length - 1, 0));
			return [promoted[idx]].concat(feed as FullPromotedPost[]);
		} else {
			return feed;
		}
	}

	for (let i = 0; i < insertTotal; i++) {
		// Copy the promoted post into the density slot
		if (filtered[promotedIdx]) {
			merged.push(filtered[promotedIdx]);
			promotedIdx++;
		}
		// Copy density -1 feed items
		merged = merged.concat(feed.slice(feedIdx, feedIdx + density - 1));
		// keep track of index for next iteration
		feedIdx = feedIdx + density - 1;
	}
	// Copy the remaining feed items
	return merged.concat(feed.slice(feedIdx));
}
